import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { toPublicURL } from '@plone/volto/helpers/Url/Url';
import { useSelector } from 'react-redux';
import type { SchemaOrgProps, LDSchema, Schema, BreadcrumbItem } from './types';
import { generateSchema } from './SchemaGeneration';
import { SCHEMA_CONTEXT, toSingleRootNode } from './utils';

const SchemaOrg = ({ type, content }: SchemaOrgProps) => {
  // Get all required information from the state
  const extendedBreadcrumbs = useSelector(
    (state: {
      extendedBreadcrumbs: { items: { title: string; url: string }[] };
    }) => state?.extendedBreadcrumbs?.items,
  );
  const subrequests = useSelector(
    (state: { querystringsearch: { subrequests: Record<string, any> } }) =>
      state?.querystringsearch?.subrequests,
  );

  // Prepare the state to be passed to the schema generation function
  const state = {
    querystringsearch: {
      subrequests: subrequests,
    },
    extendedBreadcrumbs: {
      items: extendedBreadcrumbs,
    },
  };

  if (!type || !content) {
    return null;
  }

  const jsonLd: LDSchema = generateSchema({ type, content, state });

  const schemas: Schema[] = Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  const items: BreadcrumbItem[] = [];
  extendedBreadcrumbs?.forEach((item, position) => {
    items.push({
      '@type': 'ListItem',
      position: position + 1,
      item: {
        '@id': toPublicURL(item.url),
        name: item.title,
      },
    });
  });

  if (items.length > 0) {
    schemas.push({
      '@context': SCHEMA_CONTEXT,
      '@type': 'BreadcrumbList',
      itemListElement: items,
    });
  }

  if (schemas.length === 0) {
    return null;
  }

  // Safari chokes on an array as root node, see toSingleRootNode
  const result = JSON.stringify(toSingleRootNode(schemas));

  return (
    <Helmet>
      <script type="application/ld+json">{result}</script>
    </Helmet>
  );
};

export default SchemaOrg;
